import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { useIsSimulationHarnessAvailable } from './availability';
import {
  SQUAD_QUERY_PARAM,
  decodeHarnessSquad,
  readStoredHarnessSquad,
  writeStoredHarnessSquad
} from './storage';
import type { HarnessSquad } from './squad';

const readQueryParam = (
  value: string | string[] | undefined
): string | null => {
  if (typeof value === 'string') return value;
  // Next gives an array when a parameter is repeated. Take the first rather
  // than refusing: a duplicated parameter is a copy-paste artefact, not intent.
  if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
  return null;
};

export type HarnessSquadState = {
  squad: HarnessSquad | null;
  /**
   * Whether the lookup has finished. `false` means a squad may still arrive.
   *
   * Callers need this to tell "no squad" from "not looked yet". Both sources
   * are browser-only and the page server-renders, so the first client render
   * always reports no squad even when one exists, and anything that acts on
   * that gap acts on a false negative.
   */
  isResolved: boolean;
};

/**
 * The fabricated squad currently driving the pitch, or null for ordinary use.
 *
 * The availability gate lives inside this hook on purpose. It is the only way
 * to obtain a harness squad, so a caller cannot reach one without passing the
 * gate, and a future surface cannot forget to check.
 *
 * A squad in the URL wins over a stored one and replaces it, so opening a
 * shared link puts you on the same team as whoever sent it rather than
 * silently keeping your own.
 */
export const useHarnessSquad = (): HarnessSquadState => {
  const isAvailable = useIsSimulationHarnessAvailable();
  const { isReady, query } = useRouter();
  const [squad, setSquad] = useState<HarnessSquad | null>(null);
  const [isResolved, setIsResolved] = useState(false);

  const encodedFromUrl = readQueryParam(query[SQUAD_QUERY_PARAM]);

  useEffect(() => {
    if (!isAvailable) {
      setSquad(null);
      // Left unresolved on purpose, even though the returned value reports
      // resolved while unavailable. Availability is false on every first
      // render, because `useAuth0User` has no user until the session loads,
      // so marking the internal state resolved here would still be set when
      // it flips true. The hook would then claim to have looked before the
      // effect had re-run, and the caller would fetch `/live/team` in that
      // gap: pre-season that 404s, and react-query keeps the error after the
      // query is disabled, which is the frozen pitch this flag exists to
      // prevent.
      return;
    }
    // Query parsing is only populated once the router has hydrated; acting
    // earlier would read an empty query and fall back to storage, briefly
    // showing the wrong team to somebody who opened a shared link.
    if (!isReady) return;

    if (encodedFromUrl !== null) {
      const shared = decodeHarnessSquad(encodedFromUrl);
      if (shared !== null) {
        writeStoredHarnessSquad(shared);
        setSquad(shared);
        setIsResolved(true);
        return;
      }
    }

    setSquad(readStoredHarnessSquad());
    setIsResolved(true);
  }, [isAvailable, isReady, encodedFromUrl]);

  return {
    squad: isAvailable ? squad : null,
    isResolved: isAvailable ? isResolved : true
  };
};
